import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, ChevronLeft, Calendar, Users, Heart, Wallet,
  Mountain, Compass, BookOpen, Sparkles, UtensilsCrossed, User,
  Users2, UsersRound, Loader2, MapPin, Phone
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quiz Steps Data
const DURATION_OPTIONS = [
  { value: '1-2 days', label: '1-2 Days', desc: 'Quick getaway', icon: Calendar },
  { value: '3-4 days', label: '3-4 Days', desc: 'Perfect weekend', icon: Calendar },
  { value: '5-7 days', label: '5-7 Days', desc: 'Full experience', icon: Calendar },
  { value: '7+ days', label: '7+ Days', desc: 'Deep exploration', icon: Calendar },
];

const INTEREST_OPTIONS = [
  { value: 'nature', label: 'Nature & Wildlife', desc: 'Mountains, waterfalls, flora', icon: Mountain },
  { value: 'adventure', label: 'Adventure', desc: 'Trekking, exploration', icon: Compass },
  { value: 'culture', label: 'Culture & Heritage', desc: 'Temples, tribal villages', icon: BookOpen },
  { value: 'relaxation', label: 'Relaxation', desc: 'Spa, peaceful retreats', icon: Sparkles },
  { value: 'food', label: 'Food & Coffee', desc: 'Local cuisine, plantations', icon: UtensilsCrossed },
];

const GROUP_OPTIONS = [
  { value: 'solo', label: 'Solo', desc: 'Just me', icon: User },
  { value: 'couple', label: 'Couple', desc: 'Romantic getaway', icon: Heart },
  { value: 'family', label: 'Family', desc: 'With kids', icon: Users2 },
  { value: 'group', label: 'Group', desc: 'Friends/colleagues', icon: UsersRound },
];

const BUDGET_OPTIONS = [
  { value: 'budget', label: 'Budget-Friendly', desc: '~₹3,000-5,000/day', icon: Wallet },
  { value: 'moderate', label: 'Moderate', desc: '~₹5,000-8,000/day', icon: Wallet },
  { value: 'premium', label: 'Premium', desc: '~₹8,000-15,000/day', icon: Wallet },
  { value: 'luxury', label: 'Luxury', desc: '₹15,000+/day', icon: Wallet },
];

const STEPS = [
  { id: 'duration', title: 'Trip Duration', subtitle: 'How long is your trip?', options: DURATION_OPTIONS, multi: false },
  { id: 'interests', title: 'Your Interests', subtitle: 'What excites you? (Select multiple)', options: INTEREST_OPTIONS, multi: true },
  { id: 'group', title: 'Who\'s Traveling?', subtitle: 'Tell us about your group', options: GROUP_OPTIONS, multi: false },
  { id: 'budget', title: 'Budget Range', subtitle: 'Your preferred spending level', options: BUDGET_OPTIONS, multi: false },
  { id: 'contact', title: 'Almost There!', subtitle: 'Share your details so we can assist you better', options: null, multi: false },
];

export function TripPlannerQuiz({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selections, setSelections] = useState({
    duration: '',
    interests: [],
    group: '',
    budget: '',
    name: '',
    mobile: '',
  });

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleSelect = (value) => {
    if (step.multi) {
      setSelections(prev => ({
        ...prev,
        [step.id]: prev[step.id].includes(value)
          ? prev[step.id].filter(v => v !== value)
          : [...prev[step.id], value]
      }));
    } else {
      setSelections(prev => ({ ...prev, [step.id]: value }));
    }
  };

  const canProceed = () => {
    if (step.id === 'interests') return selections.interests.length > 0;
    if (step.id === 'contact') return selections.name.trim() && selections.mobile.trim().length >= 10;
    return selections[step.id];
  };

  const handleNext = async () => {
    if (isLastStep) {
      await generateItinerary();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const generateItinerary = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/itinerary/generate`, {
        name: selections.name,
        mobile: selections.mobile,
        preferences: {
          duration: selections.duration,
          interests: selections.interests,
          group_size: selections.group,
          budget: selections.budget,
        }
      });
      
      toast.success('Your personalized itinerary is ready!');
      onOpenChange(false);
      navigate(`/itinerary/${response.data.itinerary_id}`);
    } catch (error) {
      console.error('Failed to generate itinerary:', error);
      toast.error('Failed to generate itinerary. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setSelections({
      duration: '',
      interests: [],
      group: '',
      budget: '',
      name: '',
      mobile: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) resetQuiz(); }}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden" data-testid="trip-planner-modal">
        {/* Progress Bar */}
        <div className="h-1.5 bg-muted">
          <motion.div 
            className="h-full bg-emerald-600"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Step {currentStep + 1} of {STEPS.length}
              </p>
              <DialogTitle className="font-serif text-2xl">{step.title}</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">{step.subtitle}</p>
            </div>
            <MapPin className="w-8 h-8 text-emerald-600" />
          </div>
        </DialogHeader>

        <div className="px-6 py-4 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {step.options ? (
                <div className={`grid gap-3 ${step.multi ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
                  {step.options.map((option) => {
                    const isSelected = step.multi 
                      ? selections[step.id].includes(option.value)
                      : selections[step.id] === option.value;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                          isSelected 
                            ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950/50' 
                            : 'border-border hover:border-emerald-300 hover:bg-muted/50'
                        }`}
                        data-testid={`option-${option.value}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                            <option.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{option.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{option.desc}</p>
                          </div>
                        </div>
                        {isSelected && step.multi && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Contact Form */
                <div className="space-y-5">
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-xl border border-emerald-200 dark:border-emerald-800">
                    <p className="text-sm text-emerald-800 dark:text-emerald-200">
                      Your contact details help us serve you better. We'll only reach out to assist with your travel plans and ensure the best experience.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        Your Name
                      </Label>
                      <Input
                        id="name"
                        placeholder="Enter your full name"
                        value={selections.name}
                        onChange={(e) => setSelections(prev => ({ ...prev, name: e.target.value }))}
                        className="h-12"
                        data-testid="input-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="mobile" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        Mobile Number
                      </Label>
                      <Input
                        id="mobile"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={selections.mobile}
                        onChange={(e) => setSelections(prev => ({ ...prev, mobile: e.target.value }))}
                        className="h-12"
                        data-testid="input-mobile"
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-muted/50 border-t flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0 || isGenerating}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canProceed() || isGenerating}
            className="btn-emerald gap-2"
            data-testid="quiz-next-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating Your Trip...
              </>
            ) : isLastStep ? (
              <>
                Get My Itinerary
                <Sparkles className="w-4 h-4" />
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Button to trigger the quiz - now links to Emerald AI page
export function PlanMyTripButton({ className = '', variant = 'default' }) {
  if (variant === 'hero') {
    return (
      <a
        href="/emerald-ai"
        className={`btn-gold inline-flex items-center justify-center gap-2 text-sm sm:text-base py-2.5 sm:py-3 ${className}`}
        data-testid="plan-my-trip-btn"
      >
        <MapPin className="w-4 h-4" />
        Plan My Trip
      </a>
    );
  }

  return (
    <a
      href="/emerald-ai"
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="plan-my-trip-btn"
    >
      <Button className="gap-2">
        <MapPin className="w-4 h-4" />
        Plan My Trip
      </Button>
    </a>
  );
}
